import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Coins,
  Crosshair,
  Crown,
  FileText,
  Gamepad2,
  Lightbulb,
  ListOrdered,
  MapPin,
  Route,
  Search,
  Settings,
  Smartphone,
  Timer,
  TrendingUp,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  COIN_RANGES,
  filterCoinDays,
  formatCoins,
  snapshotFor,
  type CoinDay,
  type CoinEntry,
  type CoinGrain,
  type CoinModule,
  type CoinModuleFilter,
  type CoinRangeId,
  type CoinSnapshot,
  type CoinSort,
} from '../../data/coin-usage-mock';
import { CoinBalanceArt, CoinEmptyArt, CoinSkyline, SessionMap } from './CoinArt';
import { ModuleDonut, UsageChart, moduleIcon } from './CoinCharts';
import './coin-usage.css';

const GRAINS: Array<{ id: CoinGrain; label: string }> = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
];

const CHIPS: Array<{ id: CoinModuleFilter; label: string; icon?: LucideIcon }> = [
  { id: 'all', label: 'All' },
  { id: 'stationary', label: 'Stationary', icon: MapPin },
  { id: 'playlist', label: 'Playlist', icon: ListOrdered },
  { id: 'itinerary', label: 'Itinerary', icon: Route },
  { id: 'joystick', label: 'Joystick', icon: Gamepad2 },
];

const ROW_ICON: Record<CoinEntry['rows'][number]['id'], LucideIcon> = {
  location: MapPin,
  started: Clock,
  ended: Timer,
  duration: Timer,
  coins: Coins,
  accuracy: Crosshair,
  device: Smartphone,
  note: FileText,
};

const PREVIEW_MS = 680;

export interface CoinUsageViewProps {
  /**
   * Leave unset to preview skeletons once.
   * Pass a real flag once the usage API is wired.
   */
  loading?: boolean;
  /**
   * Leave unset to use the built-in sample for the selected range.
   * Pass null when the API returns an empty range.
   * Keep the object reference stable.
   */
  data?: CoinSnapshot | null;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), PREVIEW_MS);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`cu-skel ${className}`} />;
}

function entryCount(count: number): string {
  return `${count} ${count === 1 ? 'entry' : 'entries'}`;
}

function findEntry(days: CoinDay[], id: string | null): CoinEntry | null {
  if (!id) return null;
  for (let index = 0; index < days.length; index += 1) {
    const found = days[index].entries.find((row) => row.id === id);
    if (found) return found;
  }
  return null;
}

function chartHint(points: CoinSnapshot['series'][CoinGrain], hint: string): string {
  if (points.some((point) => point.hint === hint)) return hint;
  return points.reduce((best, point) => (point.coins > best.coins ? point : best)).hint;
}

function SpendDelta({ value, text }: { value: number; text: string }) {
  const lower = value <= 0;
  const Icon = lower ? ArrowDown : ArrowUp;
  return (
    <p className={`cu-delta ${lower ? 'is-good' : 'is-up'}`}>
      <Icon size={14} aria-hidden="true" />
      <span>{`${Math.abs(value)}% ${text}`}</span>
    </p>
  );
}

function BalanceSkeleton() {
  return (
    <div className="cu-balance cu-balance--skel" data-testid="skeleton-balance" aria-hidden="true">
      <Skel className="cu-skel--line" />
      <Skel className="cu-skel--value" />
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="cu-kpis" data-testid="skeleton-kpis" aria-hidden="true">
      {[0, 1, 2, 3].map((key) => (
        <div key={key} className="cu-kpi">
          <Skel className="cu-skel--ico" />
          <Skel className="cu-skel--line" />
          <Skel className="cu-skel--value" />
          <Skel className="cu-skel--short" />
        </div>
      ))}
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="cu-board" aria-hidden="true">
      <div className="cu-col">
        <section className="cu-card" data-testid="skeleton-analytics">
          <Skel className="cu-skel--title" />
          <Skel className="cu-skel--plot" />
        </section>
        <section className="cu-card" data-testid="skeleton-modules">
          <Skel className="cu-skel--title" />
          <div className="cu-skel-row">
            <Skel className="cu-skel--circle" />
            <div style={{ flex: 1 }}>
              <Skel className="cu-skel--line" />
              <Skel className="cu-skel--line" />
              <Skel className="cu-skel--line" />
              <Skel className="cu-skel--short" />
            </div>
          </div>
        </section>
        <section className="cu-card" data-testid="skeleton-help">
          <Skel className="cu-skel--title" />
          <Skel className="cu-skel--line" />
          <Skel className="cu-skel--short" />
        </section>
        <section className="cu-card" data-testid="skeleton-insights">
          <Skel className="cu-skel--title" />
          <Skel className="cu-skel--plot" />
        </section>
      </div>
      <section className="cu-card" data-testid="skeleton-history">
        <Skel className="cu-skel--title" />
        <Skel className="cu-skel--line" />
        <Skel className="cu-skel--row" />
        <Skel className="cu-skel--row" />
        <Skel className="cu-skel--row" />
        <Skel className="cu-skel--row" />
      </section>
      <section className="cu-card cu-col--details" data-testid="skeleton-details">
        <Skel className="cu-skel--title" />
        <Skel className="cu-skel--line" />
        <Skel className="cu-skel--plot" />
        <Skel className="cu-skel--line" />
        <Skel className="cu-skel--line" />
        <Skel className="cu-skel--short" />
      </section>
    </div>
  );
}

function EmptyState() {
  return (
    <section className="cu-empty" data-testid="coins-empty">
      <CoinEmptyArt />
      <h2>No coin usage in this range</h2>
      <p>Sessions that spend MockCoins will show up here, with the module, time, and cost.</p>
    </section>
  );
}

function DetailsPlaceholder() {
  return (
    <div className="cu-placeholder" data-testid="coins-details-placeholder">
      <CoinEmptyArt compact />
      <h3>Select an activity</h3>
      <p>Open a row in Usage History to see the session, map, and coin cost.</p>
    </div>
  );
}

const CoinUsageView: React.FC<CoinUsageViewProps> = ({ loading: loadingProp, data }) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const locked = data !== undefined;
  const [range, setRange] = useState<CoinRangeId>('month');
  const [grain, setGrain] = useState<CoinGrain>('daily');
  const [moduleFilter, setModuleFilter] = useState<CoinModuleFilter>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<CoinSort>('newest');
  const [selectedId, setSelectedId] = useState<string | null>('ny-start');
  const [activeHint, setActiveHint] = useState<string | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef<HTMLDivElement>(null);
  const searchId = useId();
  const sortId = useId();
  const detailTitleId = useId();

  const sourceKey = data === undefined ? `local:${range}` : data === null ? 'empty' : data.id;

  useEffect(() => {
    setGrain('daily');
    setModuleFilter('all');
    setQuery('');
    setSort('newest');
    setRangeOpen(false);
    setActiveHint(null);
    const snap = data === undefined ? snapshotFor(range) : data;
    setSelectedId(snap?.days[0]?.entries[0]?.id ?? null);
  }, [sourceKey, data, range]);

  useEffect(() => {
    if (!rangeOpen) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (rangeRef.current && !rangeRef.current.contains(event.target as Node)) {
        setRangeOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setRangeOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [rangeOpen]);

  const local = useMemo(() => snapshotFor(range), [range]);
  const shown = data === undefined ? local : data;
  const isEmpty = !loading && (shown == null || shown.empty);
  const rangeLabel = shown?.rangeLabel || local.rangeLabel;

  const days = useMemo(
    () => (shown && !shown.empty ? filterCoinDays(shown.days, { module: moduleFilter, query, sort }) : []),
    [shown, moduleFilter, query, sort]
  );
  const selected = shown && !shown.empty && days.some((item) => item.entries.some((row) => row.id === selectedId))
    ? findEntry(shown.days, selectedId)
    : null;

  const points = shown && !shown.empty ? shown.series[grain] : [];
  const hint = shown && !shown.empty ? chartHint(points, activeHint || shown.highlight[grain]) : '';

  const pickRange = (next: CoinRangeId) => {
    setRange(next);
    setRangeOpen(false);
  };

  return (
    <div className="cu" data-testid="coins-screen" aria-busy={loading}>
      {loading && (
        <p className="visually-hidden" role="status">
          Loading coin usage
        </p>
      )}

      <header className="cu-head">
        {!isEmpty && (
          <div className="cu-sky" aria-hidden="true">
            <CoinSkyline />
          </div>
        )}
        <div className="cu-head__side">
          <div className="cu-range" ref={rangeRef}>
            <button
              type="button"
              className="cu-range__btn"
              aria-expanded={rangeOpen}
              aria-haspopup="menu"
              aria-label={`Date range, ${rangeLabel}`}
              disabled={loading || locked}
              onClick={() => setRangeOpen((open) => !open)}
            >
              <Calendar size={16} aria-hidden="true" />
              <span>{rangeLabel}</span>
              <ChevronDown size={16} aria-hidden="true" />
            </button>
            {rangeOpen && (
              <div className="cu-range__menu" role="menu" aria-label="Date range">
                {COIN_RANGES.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    aria-current={item.id === range}
                    onClick={() => pickRange(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {loading && <BalanceSkeleton />}
          {!loading && shown && !shown.empty && (
            <Link
              className="cu-balance"
              to="/app/account"
              aria-label={`Current balance, ${formatCoins(shown.balance)} coins`}
            >
              <span>
                <span className="cu-balance__label">Current Balance</span>
                <strong>{formatCoins(shown.balance)}</strong>
              </span>
              <CoinBalanceArt />
              <ChevronRight className="cu-balance__chevron" size={18} aria-hidden="true" />
            </Link>
          )}
        </div>
      </header>

      {loading && (
        <>
          <KpiSkeleton />
          <BoardSkeleton />
        </>
      )}
      {isEmpty && <EmptyState />}
      {!loading && shown && !shown.empty && (
        <>
          <section className="cu-kpis" aria-label="Coin summary">
            <article className="cu-kpi">
              <span className="cu-kpi__corner" aria-hidden="true">
                <BarChart3 size={16} />
              </span>
              <div className="cu-kpi__lead">
                <span className="cu-bubble cu-bubble--gold">
                  <Coins size={18} aria-hidden="true" />
                </span>
                <div>
                  <p className="cu-kpi__label">Total Coins Used</p>
                  <p className="cu-kpi__value">{formatCoins(shown.totalUsed)}</p>
                  <SpendDelta value={shown.totalDelta} text={shown.totalDeltaText} />
                </div>
              </div>
            </article>
            <article className="cu-kpi">
              <span className="cu-kpi__corner" aria-hidden="true">
                <Calendar size={16} />
              </span>
              <div className="cu-kpi__lead">
                <span className="cu-bubble cu-bubble--playlist">
                  <Calendar size={18} aria-hidden="true" />
                </span>
                <div>
                  <p className="cu-kpi__label">Avg. Daily Usage</p>
                  <p className="cu-kpi__value">{shown.avgDaily}</p>
                  <SpendDelta value={shown.avgDelta} text={shown.avgDeltaText} />
                </div>
              </div>
            </article>
            <article className="cu-kpi">
              <span className="cu-kpi__corner" aria-hidden="true">
                <TrendingUp size={16} />
              </span>
              <p className="cu-kpi__label">Most Used Module</p>
              <p className="cu-kpi__name">
                <MapPin className="cu-pin--blue" size={18} aria-hidden="true" />
                {shown.mostUsed.label}
              </p>
              <p className="cu-kpi__meta">
                {shown.mostUsed.coins} coins · {shown.mostUsed.pct}%
              </p>
            </article>
            <article className="cu-kpi">
              <span className="cu-kpi__corner" aria-hidden="true">
                <Crown size={16} />
              </span>
              <p className="cu-kpi__label">Top Activity</p>
              <p className="cu-kpi__name">
                <MapPin className="cu-pin--pink" size={18} aria-hidden="true" />
                {shown.topActivity.label}
              </p>
              <p className="cu-kpi__meta">
                {shown.topActivity.coins} coins · {shown.topActivity.pct}%
              </p>
            </article>
          </section>

          <div className="cu-board">
            <div className="cu-col">
              <section className="cu-card" aria-labelledby="coin-analytics-title">
                <div className="cu-card__head">
                  <h2 id="coin-analytics-title">Usage Analytics</h2>
                  <div className="cu-seg" role="group" aria-label="Chart period">
                    {GRAINS.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        aria-pressed={grain === item.id}
                        onClick={() => {
                          setGrain(item.id);
                          setActiveHint(null);
                        }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                <UsageChart
                  points={points}
                  activeHint={hint}
                  onPick={setActiveHint}
                  label={`${GRAINS.find((item) => item.id === grain)?.label} coin usage`}
                />
              </section>

              <section className="cu-card" aria-labelledby="coin-module-title">
                <h2 id="coin-module-title">Usage by Module</h2>
                <ModuleDonut total={shown.totalUsed} slices={shown.modules} />
              </section>

              <section className="cu-help" aria-labelledby="coin-help-title">
                <h2 id="coin-help-title">Need Help?</h2>
                <p>Learn how coins work and ways to save coins.</p>
                <Link className="cu-help__link" to="/app/help">
                  View Guide
                  <ChevronRight size={16} aria-hidden="true" />
                </Link>
              </section>

              <section className="cu-insights" aria-labelledby="coin-insights-title">
                <h2 id="coin-insights-title">
                  <span className="cu-insights__mark" aria-hidden="true">
                    <Lightbulb size={16} />
                  </span>
                  Quick Insights
                </h2>
                <div className="cu-insights__grid">
                  {shown.insights.map((insight) => {
                    const Icon = insight.tone === 'green' ? ArrowDown : insight.tone === 'purple' ? Calendar : Coins;
                    return (
                      <article key={insight.id} className={`cu-insight cu-insight--${insight.tone}`}>
                        <span className="cu-insight__ico" aria-hidden="true">
                          <Icon size={16} />
                        </span>
                        <strong>{insight.title}</strong>
                        <p>{insight.body}</p>
                      </article>
                    );
                  })}
                </div>
              </section>
            </div>

            <section className="cu-card cu-col cu-col--history" aria-labelledby="coin-history-title">
              <div className="cu-card__head">
                <h2 id="coin-history-title">Usage History</h2>
                <label className="cu-sort" htmlFor={sortId}>
                  <span className="visually-hidden">Sort usage history</span>
                  <BarChart3 size={14} aria-hidden="true" />
                  <select
                    id={sortId}
                    aria-label="Sort usage history"
                    value={sort}
                    onChange={(event) => setSort(event.target.value as CoinSort)}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </label>
              </div>
              <div className="cu-search">
                <Search size={16} aria-hidden="true" />
                <label className="visually-hidden" htmlFor={searchId}>
                  Search activity, location or session
                </label>
                <input
                  id={searchId}
                  type="search"
                  value={query}
                  placeholder="Search activity, location or session..."
                  autoComplete="off"
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <div className="cu-chips" role="group" aria-label="Module">
                {CHIPS.map((chip) => {
                  const Icon = chip.icon;
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      aria-pressed={moduleFilter === chip.id}
                      onClick={() => setModuleFilter(chip.id)}
                    >
                      {Icon ? (
                        <Icon className={`cu-chip__ico cu-chip__ico--${chip.id}`} size={14} aria-hidden="true" />
                      ) : null}
                      {chip.label}
                    </button>
                  );
                })}
              </div>
              {days.length === 0 ? (
                <div className="cu-placeholder" data-testid="coins-history-empty">
                  <CoinEmptyArt compact />
                  <h3>No matching activity</h3>
                  <p>Try another module, or clear the search.</p>
                </div>
              ) : (
                <div className="cu-history__list">
                  {days.map((item) => (
                    <div key={item.id}>
                      <div className="cu-day">
                        <h3>{item.label}</h3>
                        <span>{entryCount(item.entries.length)}</span>
                      </div>
                      {item.entries.map((row) => {
                        const Icon = moduleIcon(row.module);
                        const on = row.id === selected?.id;
                        return (
                          <button
                            key={row.id}
                            type="button"
                            className={on ? 'cu-row is-on' : 'cu-row'}
                            aria-pressed={on}
                            onClick={() => setSelectedId(row.id)}
                          >
                            <span className={`cu-bubble cu-bubble--${row.module}`}>
                              <Icon size={16} aria-hidden="true" />
                            </span>
                            <span className="cu-row__copy">
                              <span className="cu-row__title">{row.title}</span>
                              <span className="cu-row__sub">{row.subtitle}</span>
                            </span>
                            <span className="cu-row__time">{row.time}</span>
                            <span className="cu-row__cost">
                              <Coins size={14} aria-hidden="true" />
                              {`-${row.coins}`}
                            </span>
                            <ChevronRight className="cu-row__chevron" size={16} aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="cu-card cu-col cu-col--details" aria-labelledby={detailTitleId}>
              <div className="cu-card__head">
                <h2 id={detailTitleId}>Usage Details</h2>
                {selected && (
                  <button type="button" className="cu-close" aria-label="Close usage details" onClick={() => setSelectedId(null)}>
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
              {selected ? (
                <div>
                  <div className="cu-detail__top">
                    <span className={`cu-bubble cu-bubble--${selected.module}`}>
                      {React.createElement(moduleIcon(selected.module), { size: 18, 'aria-hidden': true })}
                    </span>
                    <div className="cu-detail__copy">
                      <h3>{selected.title}</h3>
                      <p className="cu-detail__mode">{selected.mode}</p>
                    </div>
                    <span className="cu-badge">
                      <Coins size={13} aria-hidden="true" />
                      {`-${selected.coins}`}
                    </span>
                  </div>
                  <p className="cu-detail__when">{selected.when}</p>
                  <p className="cu-detail__summary">{selected.summary}</p>
                  <SessionMap map={selected.map} label={selected.mapLabel} />
                  <ul className="cu-meta">
                    {selected.rows.map((row) => {
                      const Icon = ROW_ICON[row.id];
                      return (
                        <li key={row.id}>
                          <Icon size={16} aria-hidden="true" />
                          <span className="cu-meta__label">{row.label}</span>
                          <span className="cu-meta__value">
                            {row.value}
                            {row.hint ? <small>{row.hint}</small> : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <DetailsPlaceholder />
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default CoinUsageView;
